import { useLazyQuery } from "@apollo/react-hooks";
import { Container, Grid, TextField } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import React from "react";
import _ from "lodash";
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useUntypedSelector,
} from "react-redux";
import "./App.css";
import { ANALYSIS } from "./queries";
import { AppDispatch, RootState, setText, querySlowly } from "./store";
import {
  Query,
  QueryVariables,
  Query_analysis_knp_tags,
} from "./__generated__/types";
const useSelector: TypedUseSelectorHook<RootState> = useUntypedSelector;

function App() {
  return (
    <Container maxWidth="md" style={{ marginTop: "100px" }}>
      <Grid container spacing={3} style={{ minHeight: "100vh" }}>
        <Home />
      </Grid>
    </Container>
  );
}

const Home = () => {
  const dispatch: AppDispatch = useDispatch();
  const text = useSelector((state) => state.text);
  return (
    <>
      <Grid item xs={12}>
        <TextField
          placeholder="Input text here"
          fullWidth
          variant="outlined"
          multiline
          rows={2}
          rowsMax={20}
          value={text}
          onChange={(e) => dispatch(setText(e.target.value))}
        />
      </Grid>
      <Grid item xs={12}>
        <ResultViewer />
      </Grid>
    </>
  );
};

const ResultViewer = () => {
  const text = useSelector((state) => state.text);
  const showresult = !!text;
  const dispatch = useDispatch();
  const [getResult, { error, loading, data }] = useLazyQuery<
    Query,
    QueryVariables
  >(ANALYSIS);
  if (text) {
    dispatch(
      querySlowly(
        window.setTimeout(() => {
          getResult({
            variables: { text: text },
          });
        }, 500)
      )
    );
  }
  return (
    <Grid container style={{ minWidth: "100%" }}>
      <Grid
        item
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "25vh",
        }}
      >
        {!showresult ? (
          ""
        ) : loading ? (
          <CircularProgress />
        ) : data ? (
          <Analysis data={data} />
        ) : (
          ""
        )}
      </Grid>
      <Grid item xs={4} />
    </Grid>
  );
};

interface AnalysisProps {
  data: Query;
}

const Analysis = ({ data }: AnalysisProps) => {
  const x = 50;
  const y = 100;
  const width = 1000;
  const tags = data.analysis.knp.tags;
  const dy = 20;
  const arcyOffset = y - 30;
  const defaultFontSize = 20;
  const fontStretch = 1.3;
  const fontSize = Math.min(
    (0.7 * (width - 2 * x)) / data.analysis.text.length / fontStretch,
    defaultFontSize
  );
  const dx = (width - x) / tags.length;
  const labelMargin = fontSize / 2;
  const tagx = tags.map((_, i) => {
    return x + dx * i;
  });
  const tagComps = tags.map((v, i) => (
    <TagSpan tag={v} x={tagx[i]} y={y} key={i} stretch={1.4} />
  ));

  const _tmp: [number, number][] = tags.map((v, i) => [i, v.parent]);
  const arcs = _tmp.filter(([i, j]) => i !== j);
  const arcWidths = [...new Set(arcs.map((a) => Math.abs(a[0] - a[1])).sort())];
  const arcy = arcs.map(
    (e) => arcyOffset - (arcWidths.indexOf(Math.abs(e[0] - e[1])) + 1) * dy
  );
  const arcComps = arcs.map(([start, end], i) => (
    <DepArrow
      xstart={tagx[start]}
      xend={tagx[end]}
      ybottom={arcyOffset}
      ytop={arcy[i]}
      key={i}
    />
  ));

  const char2tagpos = tags.reduce<number[]>(
    (acc, tag, i) => acc.concat(Array(tag.end - tag.start).fill(i)),
    []
  );
  const tagLengthCum = tags.reduce<number[]>(
    (acc, tag) => {
      acc.push(acc[acc.length - 1] + tag.end - tag.start);
      return acc;
    },
    [0]
  );
  const getBoundary = (tagpos: number, charpos: number) => {
    const fw = fontSize * fontStretch;
    const tag = tags[tagpos];
    const tagw = (tag.end - tag.start) * fw;
    return (
      x +
      dx * tagpos -
      tagw / 2 +
      fw * charpos -
      (fontSize * (fontStretch - 1)) / 2
    );
  };
  const entLabels = data.analysis.ents.map((v) => {
    const i = char2tagpos[v.start];
    const l = getBoundary(i, v.start - tagLengthCum[i]);
    const j = char2tagpos[v.end - 1];
    const r = getBoundary(j, v.end - tagLengthCum[j]);
    return (
      <EntLabel
        x={l - labelMargin}
        y={y - fontSize - labelMargin}
        width={r - l + labelMargin * 2}
        height={fontSize + labelMargin * 2}
        key={i}
        label={v.label}
        link={v.link}
        fontSize={fontSize}
      />
    );
  });

  const link = data.analysis.ents[0]?.link;

  return (
    <Grid container>
      <Grid item xs={12}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          id="nlpsvg"
          height="200"
          fontSize={fontSize}
          width={`${width}`}
        >
          {entLabels}
          {tagComps}
          {arcComps}
        </svg>
      </Grid>
      {link ? (
        <Grid item xs={12}>
          <iframe
            id="inlineFrameExample"
            title="Inline Frame Example"
            width="100%"
            height="300"
            src={link}
          ></iframe>
        </Grid>
      ) : (
        ""
      )}
    </Grid>
  );
};

interface EntLabelProps {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  link: string | null;
  fontSize: number;
}

const EntLabel = ({
  x,
  y,
  width,
  height,
  label,
  link,
  fontSize,
}: EntLabelProps) => {
  return (
    <g>
      <a href={link || ""}>
        <rect
          className={`entlabel  ${link ? "entlabel-href" : ""}`}
          x={x}
          y={y}
          width={width}
          height={height}
          rx={3}
        />
      </a>
      <text
        className={`ent-label-text`}
        x={x + width / 2}
        textAnchor="middle"
        y={y + height + fontSize * 1.5}
      >
        {label}
      </text>
    </g>
  );
};

interface TagSpanProps {
  tag: Query_analysis_knp_tags;
  x: number;
  y: number;
  stretch: number;
}

const getSectionFeature = (tag: Query_analysis_knp_tags) =>
  /節機能-(.+?)"/.exec(tag.featureJson)?.[1];

const TagSpan = ({ tag, x, y, stretch }: TagSpanProps) => {
  const feature = getSectionFeature(tag);
  return (
    <g>
      <text
        textAnchor="middle"
        y={`${y}`}
        x={`${x}`}
        textLength={`${tag.text.length * stretch}em`}
        className="tag-span"
      >
        {tag.text}
        {feature ? (
          <tspan className="tag-annotation" dy="2rem" x={`${x}`}>
            {feature}
          </tspan>
        ) : (
          ""
        )}
      </text>
    </g>
  );
};

interface DepArrowProps {
  xstart: number;
  xend: number;
  ybottom: number;
  ytop: number;
}

const DepArrow = ({ xstart, xend, ybottom, ytop }: DepArrowProps) => {
  return (
    <g>
      <path
        className="dep-arrow"
        d={`M ${xstart} ${ybottom} L ${xstart} ${ytop} L ${xend} ${ytop} L ${xend} ${ybottom}`}
      />
    </g>
  );
};

export default App;
